import {connect} from "react-redux";
import {getAlLDocuments, messageReset} from "../store/documents";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {useEffect, useState, useCallback, useMemo} from "react";
import {getAllVerifyResponse} from "../store/logIn";
import {FormControl, InputAdornment, InputLabel, MenuItem, OutlinedInput, Select, Skeleton} from "@mui/material";
import Header from "../blocks/header";
import Dashboard from "../blocks/dashboard";
import Toolbar from "@mui/material/Toolbar";
import * as React from "react";
import Box from "@mui/material/Box";
import Copyright from "../blocks/copyright";
import ReactPaginate from "react-paginate";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import Button from "@mui/material/Button";
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from 'axios';

// Create axios instance for better performance
const apiClient = axios.create({
    baseURL: 'https://fx.softmakon.uz/api/',
    timeout: 10000,
});

// Add request interceptor for caching
const requestCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

apiClient.interceptors.request.use(
    (config) => {
        const cacheKey = `${config.url}?${JSON.stringify(config.params)}`;
        const cached = requestCache.get(cacheKey);

        if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
            config.adapter = () => Promise.resolve({
                data: cached.data,
                status: 200,
                statusText: 'OK',
                headers: {},
                config,
                request: {}
            });
        }

        return config;
    }
);

apiClient.interceptors.response.use(
    (response) => {
        const cacheKey = `${response.config.url}?${JSON.stringify(response.config.params)}`;
        requestCache.set(cacheKey, {
            data: response.data,
            timestamp: Date.now()
        });
        return response;
    }
);

// Debounce hook
function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

// Loading skeleton component
const TableSkeleton = () => (
    <table className="table table-hover table-responsive">
        <thead>
        <tr>
            <th scope="col">#</th>
            <th scope="col">Buyurtma kodi</th>
            <th scope="col">Partiya raqami</th>
            <th scope="col">Trek kod</th>
            <th scope="col">Turi</th>
            <th scope="col">Scan qilingan</th>
            <th scope="col">Scan qilinmagan</th>
            <th scope="col">Yetkazib berilmoqda</th>
            <th scope="col">Scan qilish</th>
        </tr>
        </thead>
        <tbody>
        {[...Array(10)].map((_, index) => (
            <tr key={index}>
                <td><Skeleton variant="text" width={30} /></td>
                <td><Skeleton variant="text" width={100} /></td>
                <td><Skeleton variant="text" width={80} /></td>
                <td><Skeleton variant="text" width={120} /></td>
                <td><Skeleton variant="rectangular" width={60} height={24} /></td>
                <td><Skeleton variant="text" width={40} /></td>
                <td><Skeleton variant="text" width={40} /></td>
                <td><Skeleton variant="text" width={40} /></td>
                <td><Skeleton variant="rectangular" width={80} height={36} /></td>
            </tr>
        ))}
        </tbody>
    </table>
);

function Documents(props) {
    const navigate = useNavigate()
    const params = useParams()
    const [searchParams] = useSearchParams()

    // Memoized parameter extraction
    const { trackType, serialNumber } = useMemo(() => {
        let type = searchParams.get('type')
        const serial = params['str']

        if (!type) {
            if (searchParams.has('avia')) {
                type = 'avia'
            } else if (searchParams.has('fura')) {
                type = 'fura'
            }
        }

        return { trackType: type, serialNumber: serial }
    }, [searchParams, params])

    // State management
    const [scan, setScan] = useState(() => {
        const saved = localStorage.getItem('scan');
        return saved ? JSON.parse(saved) : 1;
    })
    const [documents, setDocuments] = useState([])
    const [scanTrack, setScanTrack] = useState([])
    const [filteredDocuments, setFilteredDocuments] = useState(false)
    const [searchWord, setSearchWord] = useState('')
    const [trackingData, setTrackingData] = useState([])
    const [aviaTrackData, setAviaTrackData] = useState([])
    const [combinedData, setCombinedData] = useState([])
    const [itemsPerPage] = useState(20) // Increased for better performance
    const [itemOffset, setItemOffset] = useState(0)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    // Debounced search
    const debouncedSearchWord = useDebounce(searchWord, 300)

    // Memoized deduplication function
    const deduplicateByClientCode = useCallback((data) => {
        return data.reduce((acc, current) => {
            const existingDoc = acc.find(item => item.code === current.code);
            if (!existingDoc) {
                acc.push(current);
            } else {
                // If duplicate found, prioritize 'china' location over 'uzb' for scanning purposes
                if (current.place === 'china' && existingDoc.place === 'uzb') {
                    const index = acc.findIndex(item => item.code === current.code);
                    acc[index] = current;
                }
            }
            return acc;
        }, []);
    }, []);

    // Optimized data fetching
    const fetchTrackingData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            let combinedTrackingData = [];

            if (trackType) {
                // Single API call for specific type
                const response = await apiClient.get('combined-tracking/', {
                    params: {
                        type: trackType,
                        serial_number: serialNumber,
                        limit: 100 // Fetch more data in single request
                    }
                });

                const result = response.data;

                if (trackType === 'fura') {
                    setTrackingData(result);
                    combinedTrackingData = result.map(item => ({
                        code: item.clientCode || item.code,
                        serial_number: item.serial_number,
                        track_code: item.trackingNumber || item.track_code,
                        place: item.location,
                        type: 'fura',
                        weight: item.weight,
                        status: item.status,
                        confirmed: item.confirmed,
                        boxId: item.boxId,
                        all_sum: item.all_sum,
                        createdAt: item.createdAt
                    }));
                } else if (trackType === 'avia') {
                    setAviaTrackData(result);
                    combinedTrackingData = result.map(item => ({
                        code: item.code,
                        serial_number: item.serial_number,
                        track_code: item.track_code,
                        place: item.place,
                        type: 'avia',
                        already_come: item.already_come,
                        create_date: item.create_date
                    }));
                }

                // Filter by serial number if needed
                if (serialNumber) {
                    combinedTrackingData = combinedTrackingData.filter(item =>
                        item.serial_number && item.serial_number.toLowerCase() === serialNumber.toLowerCase()
                    );
                }
            } else {
                // Parallel requests for better performance
                const requests = [
                    apiClient.get('combined-tracking/', {params: {type: 'avia', limit: 50}}),
                    apiClient.get('combined-tracking/', {params: {type: 'fura', limit: 50}})
                ];

                const [aviaResponse, furaResponse] = await Promise.all(requests);

                const aviaResult = aviaResponse.data;
                const furaResult = furaResponse.data;

                setAviaTrackData(aviaResult);
                setTrackingData(furaResult);

                // Combine and normalize the data
                combinedTrackingData = [
                    ...furaResult.map(item => ({
                        code: item.clientCode || item.code,
                        serial_number: item.serial_number,
                        track_code: item.trackingNumber || item.track_code,
                        place: item.location,
                        type: 'fura',
                        weight: item.weight,
                        status: item.status,
                        confirmed: item.confirmed,
                        boxId: item.boxId,
                        all_sum: item.all_sum,
                        createdAt: item.createdAt
                    })),
                    ...aviaResult.map(item => ({
                        code: item.code,
                        serial_number: item.serial_number,
                        track_code: item.track_code,
                        place: item.place,
                        type: 'avia',
                        already_come: item.already_come,
                        create_date: item.create_date
                    }))
                ];

                if (serialNumber) {
                    combinedTrackingData = combinedTrackingData.filter(item =>
                        item.serial_number && item.serial_number.toLowerCase() === serialNumber.toLowerCase()
                    );
                }
            }

            const deduplicatedData = deduplicateByClientCode(combinedTrackingData);
            setCombinedData(deduplicatedData);
        } catch (error) {
            console.error('Error fetching tracking data:', error);
            setError('Ma\'lumotlarni yuklashda xatolik yuz berdi');
        } finally {
            setLoading(false);
        }
    }, [trackType, serialNumber, deduplicateByClientCode]);

// localStorage'ga saqlash
    useEffect(() => {
        localStorage.setItem("scan", JSON.stringify(scan));
    }, [scan]);

// URL parametrlari o'zgarganda faqat search va offsetni reset qilish
    useEffect(() => {
        setSearchWord('')
        setFilteredDocuments(false)
        // setScan(1)  ← Buni OLIB TASHLANG! localStorage saqlanadi
        setItemOffset(0)
    }, [trackType, serialNumber])


    // Initial data loading
    useEffect(() => {
        if (!trackType) {
            props.getAlLDocuments(params['str'])
        }
        fetchTrackingData()
    }, [trackType, serialNumber, fetchTrackingData])

    // Process documents data
    useEffect(() => {
        if (!trackType && props.documents.length > 0) {
            const deduplicatedDocs = deduplicateByClientCode(props.documents);
            setDocuments(deduplicatedDocs)
        } else {
            setDocuments([])
        }
    }, [props.documents, trackType, deduplicateByClientCode])

    // Generate scan track data with ontheway support
    const scanTrackData = useMemo(() => {
        let allRawData = [];

        if (!trackType && props.documents.length > 0) {
            allRawData = [...props.documents];
        }

        if (trackingData.length > 0) {
            trackingData.forEach(item => {
                allRawData.push({
                    code: item.clientCode || item.code,
                    serial_number: item.serial_number,
                    place: item.location
                });
            });
        }

        if (aviaTrackData.length > 0) {
            aviaTrackData.forEach(item => {
                allRawData.push({
                    code: item.code,
                    serial_number: item.serial_number,
                    place: item.place || 'china'
                });
            });
        }

        if (allRawData.length === 0) return [];

        return Object.values(allRawData.reduce((hash, item) => {
            const key = `${item.code}-${item.serial_number || 'no-serial'}`;

            if (!hash[key]) {
                hash[key] = {
                    serial_number: item.serial_number,
                    code: item.code,
                    uzb: [],
                    china: [],
                    ontheway: []
                };
            }

            if (item.place === 'uzb') {
                hash[key].uzb.push(item.place);
            } else if (item.place === 'ontheway') {
                hash[key].ontheway.push(item.place);
            } else {
                hash[key].china.push(item.place);
            }
            return hash;
        }, {}));
    }, [props.documents, trackingData, aviaTrackData, trackType]);

    useEffect(() => {
        setScanTrack(scanTrackData);
    }, [scanTrackData]);

    // Save scan preference to localStorage
    useEffect(() => {
        localStorage.setItem("scan", JSON.stringify(scan));
    }, [scan]);

    // Memoized page items calculation
    const pageItems = useMemo(() => {
        return filteredDocuments || (trackType ? combinedData : [...documents, ...combinedData])
    }, [filteredDocuments, documents, combinedData, trackType])

    // Pagination calculations
    const { currentItems, pageCount } = useMemo(() => {
        const endOffset = itemOffset + itemsPerPage;
        const items = pageItems.slice(itemOffset, endOffset);
        const pages = Math.ceil(pageItems.length / itemsPerPage);
        return { currentItems: items, pageCount: pages };
    }, [pageItems, itemOffset, itemsPerPage]);

    // Handle page change
    const handlePageClick = useCallback((event) => {
        const newOffset = (event.selected * itemsPerPage) % pageItems.length;
        setItemOffset(newOffset);
    }, [itemsPerPage, pageItems.length]);

    // Navigation functions
    const toEdit = useCallback((e, data) => {
        e.currentTarget.blur()
        navigate(`/one-doc/${params['str']}/${data['code']}?type=${data['type'] || trackType}`)
    }, [navigate, params, trackType]);

    const goBack = useCallback((e) => {
        e.preventDefault()
        navigate('/track-type/')
    }, [navigate]);

    // Scan result functions with ontheway support
    const getScanResult = useCallback((item) => {
        const searchKey = `${item.code}-${item.serial_number || 'no-serial'}`;
        const scanItem = scanTrack.find(scan =>
            `${scan.code}-${scan.serial_number || 'no-serial'}` === searchKey
        );
        return scanItem ? scanItem.uzb.length : 0;
    }, [scanTrack]);

    const getNoScanResult = useCallback((item) => {
        const searchKey = `${item.code}-${item.serial_number || 'no-serial'}`;
        const scanItem = scanTrack.find(scan =>
            `${scan.code}-${scan.serial_number || 'no-serial'}` === searchKey
        );
        return scanItem ? scanItem.china.length : 0;
    }, [scanTrack]);

    const getOnTheWayResult = useCallback((item) => {
        const searchKey = `${item.code}-${item.serial_number || 'no-serial'}`;
        const scanItem = scanTrack.find(scan =>
            `${scan.code}-${scan.serial_number || 'no-serial'}` === searchKey
        );
        return scanItem ? scanItem.ontheway.length : 0;
    }, [scanTrack]);

    // Filter effect with debounced search and ontheway support
    useEffect(() => {
        let allRawData = [];

        if (trackType && serialNumber) {
            allRawData = [...combinedData];
        } else {
            if (!trackType && props.documents.length > 0) {
                allRawData.push(...props.documents);
            }

            if (trackingData.length > 0) {
                trackingData.forEach(item => {
                    allRawData.push({
                        code: item.clientCode || item.code,
                        serial_number: item.serial_number,
                        track_code: item.trackingNumber || item.track_code,
                        place: item.location,
                        type: 'fura',
                        weight: item.weight,
                        status: item.status,
                        confirmed: item.confirmed,
                        boxId: item.boxId,
                        all_sum: item.all_sum,
                        createdAt: item.createdAt
                    });
                });
            }

            if (aviaTrackData.length > 0) {
                aviaTrackData.forEach(item => {
                    allRawData.push({
                        code: item.code,
                        serial_number: item.serial_number,
                        track_code: item.track_code,
                        place: item.place || 'china',
                        type: 'avia',
                        already_come: item.already_come,
                        create_date: item.create_date
                    });
                });
            }
        }

        // Apply filters
        let filtered = allRawData;

        // Apply search filter
        if (debouncedSearchWord) {
            filtered = filtered.filter(item =>
                item.code.toLowerCase().includes(debouncedSearchWord.toLowerCase())
            );
        }

        // Apply scan filter with ontheway support
        if (scan !== 1 && scan !== '1') {
            filtered = filtered.filter(item => item.place === scan);
        }

        if (scan === 1 || scan === '1') {
            if (debouncedSearchWord) {
                const deduplicated = deduplicateByClientCode(filtered);
                setFilteredDocuments(deduplicated);
            } else {
                setFilteredDocuments(false);
            }
        } else {
            const deduplicated = deduplicateByClientCode(filtered);
            setFilteredDocuments(deduplicated);
        }
    }, [scan, debouncedSearchWord, props.documents, trackingData, aviaTrackData,
        trackType, serialNumber, combinedData, deduplicateByClientCode]);

    // Handle Enter key for search
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' && filteredDocuments && filteredDocuments.length > 0) {
            navigate(`/one-doc/${params['str']}/${filteredDocuments[0]['code']}?type=${filteredDocuments[0]['type'] || trackType}`)
        }
    }, [filteredDocuments, navigate, params, trackType]);

    return (
        <div>
            <div className={'page worker-page documents'}>
                <Header/>
                <Dashboard/>
                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        height: '100vh',
                        overflow: 'auto',
                    }}>
                    <Toolbar/>
                    <div className={'main'}>
                        <Button variant="outlined" startIcon={<ArrowBackIcon/>} onClick={goBack}>
                            Ortga
                        </Button>

                        {error && (
                            <div className="alert alert-danger mt-3" role="alert">
                                {error}
                            </div>
                        )}

                        <div className={'header-page'}>
                            <h2 className={'page-title'}>
                                {"Buyurtmalar"}
                                {trackType && serialNumber && (
                                    <span className="ms-2">
                                        <span className={`badge ${trackType === 'avia' ? 'bg-primary' : 'bg-success'} me-2`}>
                                            {trackType === 'avia' ? 'Avia' : 'Fura'}
                                        </span>
                                        <span className="badge bg-secondary">
                                            {serialNumber}
                                        </span>
                                    </span>
                                )}
                            </h2>
                        </div>

                        <div className={'filter-block'}>
                            <div>
                                <p>
                                    <span>{"Izlash"}:</span>
                                    <FormControl variant="outlined">
                                        <InputLabel htmlFor="search-input">{"Izlash"}</InputLabel>
                                        <OutlinedInput
                                            id="search-input"
                                            type={'text'}
                                            autoFocus
                                            onKeyDown={handleKeyDown}
                                            value={searchWord}
                                            onChange={(e) => setSearchWord(e.target.value)}
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle search"
                                                        edge="end"
                                                    >
                                                        <SearchIcon/>
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                            label={"Izlash"}
                                        />
                                    </FormControl>
                                </p>
                                <p>
                                    <span>{"Buyurtmalar"}:</span>
                                    <Box sx={{minWidth: 200}}>
                                        <FormControl fullWidth>
                                            <InputLabel id="filter-select-label">{"Filter"}</InputLabel>
                                            <Select
                                                labelId="filter-select-label"
                                                id="filter-select"
                                                value={scan}
                                                label={"Filter"}
                                                onChange={(e) => setScan(e.target.value)}
                                            >
                                                <MenuItem value={1}>{"Barchasi"}</MenuItem>
                                                <MenuItem value={'uzb'}>{"Scan qilingan"}</MenuItem>
                                                <MenuItem value={'china'}>{"Scan qilinmagan"}</MenuItem>
                                                <MenuItem value={'ontheway'}>{"Yetkazib berilmoqda"}</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </p>
                            </div>
                        </div>

                        {loading ? (
                            <TableSkeleton />
                        ) : currentItems.length ? (
                            <table className="table table-hover table-responsive">
                                <thead>
                                <tr>
                                    <th scope="col">#</th>
                                    <th scope="col">{"Buyurtma kodi"}</th>
                                    <th scope="col">{"Partiya raqami"}</th>
                                    <th scope="col">{"Trek kod"}</th>
                                    <th scope="col">{"Turi"}</th>
                                    <th scope="col">{"Scan qilingan"}</th>
                                    <th scope="col">{"Scan qilinmagan"}</th>
                                    <th scope="col">{"Yetkazib berilmoqda"}</th>
                                    <th scope="col">{"Scan qilish"}</th>
                                </tr>
                                </thead>
                                <tbody>
                                {currentItems.map((item, index) => (
                                    <tr key={`${item.code}-${item.serial_number}-${index}`}>
                                        <th scope="row">{itemOffset + index + 1}</th>
                                        <td>{item.code}</td>
                                        <td>{item.serial_number}</td>
                                        <td>{item.track_code || item.trackingNumber || '-'}</td>
                                        <td>
                                                <span className={`badge ${(item.type || trackType) === 'avia' ? 'bg-primary' : 'bg-success'}`}>
                                                    {(item.type || trackType) === 'avia' ? 'Avia' : 'Fura'}
                                                </span>
                                        </td>
                                        <td>{getScanResult(item)}</td>
                                        <td>{getNoScanResult(item)}</td>
                                        <td>{getOnTheWayResult(item)}</td>
                                        <td>
                                            <Button
                                                variant="contained"
                                                title={"O'zgartirish"}
                                                color={"success"}
                                                endIcon={<DocumentScannerIcon/>}
                                                onClick={(e) => toEdit(e, item)}
                                            />
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="text-center py-5">
                                <p className="text-muted">Ma'lumotlar topilmadi</p>
                            </div>
                        )}

                        {pageCount > 1 && !loading && (
                            <div className={'pagination'}>
                                <ReactPaginate
                                    breakLabel="..."
                                    nextLabel={<KeyboardArrowRightIcon/>}
                                    onPageChange={handlePageClick}
                                    pageRangeDisplayed={5}
                                    pageCount={pageCount}
                                    previousLabel={<KeyboardArrowLeftIcon/>}
                                    renderOnZeroPageCount={null}
                                />
                            </div>
                        )}
                    </div>
                    <Copyright/>
                </Box>
            </div>
        </div>
    )
}

export default connect(
    props => props.documents,
    {getAlLDocuments, getAllVerifyResponse, messageReset}
)(Documents)